import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import appConfig from '../../../defs/appConfig';
import { RocketComponent } from '../../components/RocketComponent';
import { AxisId, DirectionId } from '../../defs/block';
import { config } from '../../defs/config';
import {
  despawnRocketEntity,
  RocketEntity,
  spawnRocketEntity,
} from '../../entities/RocketEntity';
import { GameScene } from '../../GameScene';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { mapFromViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { setBlockEntityViewLayer } from '../../entities/BlockEntity';

// types
//-----------------------------------------------------------------------------
export type RocketEffectOptions = {
  position: Vector2;
  axis: AxisId;
  big?: boolean;
};

type AnimateProps = {
  getToPosition: (position: Vector2) => Vector2;
  iterateVector: Vector2;
};

// constants
//-----------------------------------------------------------------------------
const animationMap: Record<DirectionId, AnimateProps> = {
  // left
  left: {
    getToPosition: (position: Vector2) =>
      new Vector2(-(config.tile.size + appConfig.size.width), position.y),
    iterateVector: new Vector2(-1, 0),
  },
  // right
  right: {
    getToPosition: (position: Vector2) =>
      new Vector2(config.tile.size + appConfig.size.width, position.y),
    iterateVector: new Vector2(1, 0),
  },
  // up
  up: {
    getToPosition: (position: Vector2) =>
      new Vector2(position.x, -(config.tile.size + appConfig.size.height)),
    iterateVector: new Vector2(0, -1),
  },
  // down
  down: {
    getToPosition: (position: Vector2) =>
      new Vector2(position.x, config.tile.size + appConfig.size.height),
    iterateVector: new Vector2(0, 1),
  },
};

/*
    rocket launch effect
*/
export class RocketEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _position: Vector2;
  private readonly _axis: AxisId;
  private _big: boolean;

  // state
  private readonly _collisionTracker = getCollisionTracker();

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: RocketEffectOptions) {
    this._scene = scene;
    this._position = options.position;
    this._axis = options.axis;
    this._big = options.big ?? false;
  }

  public static assets(): string[] {
    return RocketComponent.assets();
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    // launch rockets
    switch (this._axis) {
      case 'horizontal':
        await this._launchHorizontalRockets();
        break;
      case 'vertical':
        await this._launchVerticalRockets();
        break;
    }
  }

  // private
  //-------------------------------------------------------------------------
  private async _launchHorizontalRockets() {
    await Promise.all([
      this._launchRocketFromEntity('left'),
      this._launchRocketFromEntity('right'),
    ]);
  }

  private async _launchVerticalRockets() {
    await Promise.all([
      this._launchRocketFromEntity('up'),
      this._launchRocketFromEntity('down'),
    ]);
  }

  private async _launchRocketFromEntity(direction: DirectionId) {
    // spawn rocket
    const rocketEntity = spawnRocketEntity(
      this._scene,
      this._position,
      direction,
      this._big,
    );

    // Rocket should be in front of all other puzzle blocks.
    setBlockEntityViewLayer(this._scene, rocketEntity, 'foreground');

    // launch rocket
    await this._launchRocket(rocketEntity, direction);
  }

  private async _launchRocket(
    rocketEntity: RocketEntity,
    direction: DirectionId,
  ) {
    const animationProps = animationMap[direction];
    const view = rocketEntity.view;
    const from = new Vector2(view.x, view.y);
    const mapLast = mapFromViewPosition(from);
    const map = this._scene.sessionEntity.c.map;

    // determine "to" position
    const to = animationProps.getToPosition(from);

    // solve duration from travel distance of linear ease
    const duration = from.sub(to).length() / config.sim.rocket.speed;

    // use new logic for new visuals test.
    const perpendicularDirection =
      this._axis === 'horizontal' ? new Vector2(0, 1) : new Vector2(1, 0);

    const animation = gsap.to(view, {
      x: to.x,
      y: to.y,
      duration,
      ease: 'linear',
      onUpdate: () => {
        const current = new Vector2(view.x, view.y);
        const mapCurrent = mapFromViewPosition(current);

        // iterate from last to current. this is done to handle lost frames.
        while (!mapLast.equals(mapCurrent)) {
          // iterate
          mapLast.add(animationProps.iterateVector);

          // Collide around the rocket.
          for (let i = -1; i <= 1; ++i) {
            if (!this._big && i !== 0) continue;
            const perpendicular = perpendicularDirection
              .clone()
              .multiplyScalar(i);
            this.collideAt(mapLast.clone().add(perpendicular));
          }
        }

        // Resolve rocket when it goes out of map range
        if (
          mapLast.x < 0 ||
          mapLast.x > map.columns ||
          mapLast.y < 0 ||
          mapLast.y > map.rows
        ) {
          animation.totalProgress(1);
        }
      },
    });

    await animation;

    despawnRocketEntity(this._scene, rocketEntity);
  }

  private collideAt(position: Vector2) {
    this._collisionTracker.collideAt(
      this._scene.sessionEntity.c.map,
      position,
      'attack',
      {
        type: 'rocket',
      },
    );
  }
}
